// features/projects/templates/professional/widgets/what-we-dont-know.js
// ═══════════════════════════════════════════════════════════
// WHAT WE STILL DON'T KNOW — Tier-A widget
//
// Q: "What gaps in the methodology are blocking confident
//     claims, and which ones are within my control?"
// Splits gaps into actionable (researcher can fix this session)
// vs external dependencies (waiting on data ingest, calendar).
// ═══════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { L, FONT } from '../tokens';
import { useWidgetData } from './data-provider';
import { DYNAMICS } from '../../../../../lib/continuum/simulation/methodology';
import { WidgetShell, LayerSection, CaveatStrip } from './findings-status';

const DYN_BY_KEY = Object.fromEntries(DYNAMICS.map(d => [d.key, d]));

export default function WhatWeDontKnowWidget() {
  const data = useWidgetData();
  const gaps = useMemo(() => composeGaps(data), [data]);
  const total = gaps.actionable.length + gaps.external.length;

  if (data.loading) {
    return <WidgetShell label="What We Don't Know"><div style={{ fontSize: 12, color: L.dim, fontStyle: 'italic', fontFamily: FONT.sans }}>Loading…</div></WidgetShell>;
  }

  if (total === 0) {
    return (
      <WidgetShell label="What We Don't Know">
        <div style={{ fontSize: 12, color: L.mid, lineHeight: 1.55, fontFamily: FONT.sans }}>
          No gaps surfaced. Either the methodology is fully complete (unlikely at this stage) or the gap detector hasn't enumerated all preconditions yet. Treat with skepticism.
        </div>
      </WidgetShell>
    );
  }

  // Show takeaway + caveat only when there's enough data to
  // warrant the framing. With just 1-2 minor gaps the labels
  // outweigh the content.
  const showFraming = total >= 3;

  return (
    <WidgetShell label={`What We Don't Know (${total})`}>
      {gaps.actionable.length > 0 && (
        <LayerSection label={`Actionable gaps (${gaps.actionable.length})`} accent={L.gold}>
          <ul style={{ margin: '4px 0 0 0', paddingLeft: 18, fontFamily: FONT.sans, fontSize: 12, lineHeight: 1.55, color: L.text }}>
            {gaps.actionable.map((g, i) => <li key={i} style={{ marginBottom: 4 }}>{g}</li>)}
          </ul>
        </LayerSection>
      )}
      {gaps.external.length > 0 && (
        <LayerSection label={`External dependencies (${gaps.external.length})`} accent={L.dim}>
          <ul style={{ margin: '4px 0 0 0', paddingLeft: 18, fontFamily: FONT.sans, fontSize: 12, lineHeight: 1.55, color: L.mid }}>
            {gaps.external.map((g, i) => <li key={i} style={{ marginBottom: 4 }}>{g}</li>)}
          </ul>
        </LayerSection>
      )}
      {showFraming && (
        <>
          <LayerSection label="Takeaway" accent={L.gold}>
            Gaps are the research backlog — features of the methodology being honest about its limits, not bugs.
          </LayerSection>
          <CaveatStrip text="If a gap doesn't appear in this list, the detector probably doesn't check for it yet." />
        </>
      )}
    </WidgetShell>
  );
}

function composeGaps({ comparisons = [], hypotheses = [], predictions = [], questions = [] }) {
  const actionable = [];
  const external = [];
  const substantive = comparisons.filter(c =>
    c.verdict && !['pending', 'insufficient_data', 'error'].includes(String(c.verdict).toLowerCase())
  );

  const noPhaseA = substantive.filter(c => !c.phase_a_status);
  if (noPhaseA.length > 0) {
    const names = noPhaseA.map(c => DYN_BY_KEY[c.dynamic_key]?.label || c.dynamic_key);
    actionable.push(`${noPhaseA.length} substantive finding${noPhaseA.length === 1 ? '' : 's'} not Phase-A red-teamed: ${names.join(', ')}.`);
  }

  const hypByComp = new Set(hypotheses.map(h => h.comparison_id));
  const survivedNoHyp = substantive.filter(c => c.phase_a_status && !hypByComp.has(c.id));
  if (survivedNoHyp.length > 0) {
    const names = survivedNoHyp.map(c => DYN_BY_KEY[c.dynamic_key]?.label || c.dynamic_key);
    actionable.push(`${survivedNoHyp.length} Phase-A-survived finding${survivedNoHyp.length === 1 ? '' : 's'} without registered hypothesis: ${names.join(', ')}.`);
  }

  if (questions.length === 0) {
    actionable.push('Open questions register is empty — no surfaced unknowns documented for future investigation.');
  }

  const predByComp = new Set(predictions.map(p => p.comparison_id));
  const survivedNoPred = substantive.filter(c =>
    (c.phase_a_status === 'survived' || c.phase_a_status === 'survived-respecified') && !predByComp.has(c.id)
  );
  if (survivedNoPred.length > 0) {
    const names = survivedNoPred.map(c => DYN_BY_KEY[c.dynamic_key]?.label || c.dynamic_key);
    actionable.push(`${survivedNoPred.length} survived finding${survivedNoPred.length === 1 ? '' : 's'} without forward prediction: ${names.join(', ')}.`);
  }

  const pending = comparisons.filter(c =>
    !c.verdict || ['pending', 'insufficient_data'].includes(String(c.verdict).toLowerCase())
  );
  if (pending.length > 0) {
    const names = pending.map(c => DYN_BY_KEY[c.dynamic_key]?.label || c.dynamic_key);
    external.push(`${pending.length} dynamic${pending.length === 1 ? '' : 's'} pending data on one or both sides: ${names.join(', ')}.`);
  }

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const future = predictions.filter(p =>
    p.status === 'pending' && new Date(p.resolution_date) > today
  );
  if (future.length > 0) {
    const earliest = future.slice().sort((a, b) =>
      new Date(a.resolution_date) - new Date(b.resolution_date))[0];
    external.push(`${future.length} forward prediction${future.length === 1 ? '' : 's'} awaiting resolution date (earliest: ${formatDate(earliest.resolution_date)}).`);
  }

  return { actionable, external };
}

function formatDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return String(d); }
}
